import React, { useCallback, useEffect, useLayoutEffect, useState, useMemo, useRef } from "react";
import type { CSSProperties } from "react";
import { createPortal } from "react-dom";
import { MdOutlineDownload, MdOutlineEdit, MdOutlineUpload } from "react-icons/md";
import { useNavigate } from "react-router";
import { Modal, Tooltip } from "reactstrap";
import "./rcsa.css";
import "../DocumentManagement/DocumentManagement.css";
import "../Surveys_Questions/Surveys/surveys.css";
import "../business-model-inventory/style.css";
import "../controls/style.css";
import "../Surveys_Questions/BankQuestions/question.css";
import { useTranslation } from "react-i18next";
import { CiExport } from "react-icons/ci";
import Select from 'react-select';
import ExcelJS from 'exceljs';
import { toast } from "react-toastify";
import { getRCSAByPage, updateSurveyAssessmentApi } from "src/services/question";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { uploadExcelForRCSA } from "src/services/question";
import { getUserInfo, getUserRolePermissions } from "src/services/userServicer";
import { getUserRole } from "src/reducers/userInfo";
import { fetchDataApi } from "src/services/impactareaServices";
import { getReferences } from "src/services/reference";
import { ThreeDots } from "react-loader-spinner";
import { FilterIcon } from "lucide-react";
import { DataMgmtCatalogPagination } from "src/components/configuration/dataMgmtCatalogUi";

// Debounce hook for performance optimization
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const RCSA_FILTER_POPUP_MAX_W = 400;
const RCSA_FILTER_POPUP_GAP = 4;
const RCSA_FILTER_POPUP_PAD = 8;

/**
 * Toolbar filter popover: LTR aligns popup **right** edge with the icon (like Bank questions).
 * Clamps so the panel always keeps padding from the viewport edge.
 */
function getRcsaFilterPopupStyle(
  anchor: { top: number; left: number; right: number },
  rtl: boolean,
): CSSProperties {
  if (typeof window === "undefined") {
    return { top: anchor.top + RCSA_FILTER_POPUP_GAP };
  }
  const vw = window.innerWidth;
  const y = anchor.top + RCSA_FILTER_POPUP_GAP;

  if (rtl) {
    const effectiveLeft = Math.max(anchor.left, RCSA_FILTER_POPUP_PAD);
    const maxW = Math.min(
      RCSA_FILTER_POPUP_MAX_W,
      Math.max(220, vw - effectiveLeft - RCSA_FILTER_POPUP_PAD),
    );
    return {
      top: y,
      left: effectiveLeft,
      right: "auto",
      maxWidth: maxW,
    };
  }

  const effectiveRight = Math.min(anchor.right, vw - RCSA_FILTER_POPUP_PAD);
  const rightFromViewport = vw - effectiveRight;
  const maxW = Math.min(
    RCSA_FILTER_POPUP_MAX_W,
    Math.max(220, effectiveRight - RCSA_FILTER_POPUP_PAD),
  );
  return {
    top: y,
    left: "auto",
    right: rightFromViewport,
    maxWidth: maxW,
  };
}

export default function RcsaCatalogCompliance() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPageNo] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<any[]>([]);

  const [pageSize, setPageSize] = useState(10);
  const [notAllowed, setNotAllowed] = useState(false);
  // State for quarter and year
  const [selectedQuarter, setSelectedQuarter] = useState("Q1");
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  const [userPermissions, setUserPermissions] = useState<any>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [canUpdate, setCanUpdate] = useState(false);

  // State for impacted areas
  const [impactedAreas, setImpactedAreas] = useState<any[]>([]);
  const [selectedImpactedArea, setSelectedImpactedArea] = useState<string | null>(null);

  // State for edit modal
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [selectedResponse, setSelectedResponse] = useState<string>("");
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  // Click-to-view modal for long text (Examples, Compliance Brief)
  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
  const [viewModalTitle, setViewModalTitle] = useState<string>("");
  const [viewModalContent, setViewModalContent] = useState<string>("");

  // Performance optimizations
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Track latest fetch to avoid race conditions and allow cancellation
  const latestFetchIdRef = useRef<number>(0);
  const fetchControllerRef = useRef<AbortController | null>(null);
  
  // Debounced search term for performance
  const debouncedSearchTerm = useDebounce(searchTerm, 500); // 500ms delay

  const [filterPopupOpen, setFilterPopupOpen] = useState(false);
  const [filterDraftQuarter, setFilterDraftQuarter] = useState("Q1");
  const [filterDraftYear, setFilterDraftYear] = useState(() => new Date().getFullYear());
  const [filterDraftStandardId, setFilterDraftStandardId] = useState<string | null>(null);
  const filterTriggerRef = useRef<HTMLButtonElement | null>(null);
  const [filterAnchor, setFilterAnchor] = useState<{ top: number; left: number; right: number } | null>(null);

  // Client-side filter: Standard (based on Function column)
  const [standardFilter, setStandardFilter] = useState<string | null>(null);

  // Display results count after explicit search
  const [lastSearchTerm, setLastSearchTerm] = useState<string | null>(null);
  const [lastSearchCount, setLastSearchCount] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);

  // Normalize frontend search query to match backend normalization
  const normalizeQuery = (q: string): string => {
    return (
      q
        ?.normalize('NFKC')
        .replace(/\u00A0|\u202F|\u2007/g, ' ') // NBSP-like
        .replace(/[\u200B-\u200D\uFEFF]/g, '') // zero-width chars
        .replace(/\u2013|\u2014/g, '-') // dashes
        .replace(/\u2018|\u2019/g, "'") // single quotes
        .replace(/\u201C|\u201D/g, '"') // double quotes
        .replace(/\s+/g, ' ')
        .trim() || ''
    );
  };

  /** Maps known backend ORM messages to a user-facing string; logs full text for support. */
  const rcsaErrorToastMessage = (raw: string | undefined): string => {
    const msg = String(raw || '').trim();
    if (/SurveyImpactedArea|ComplianceControlActions|not associated/i.test(msg)) {
      console.error('[RCSA API]', msg);
      return t('Risk.RCSA_backend_config_error');
    }
    return msg || t('Risk.RCSA_fetch_error');
  };

  // Unique standards (functions) from current page data
  const standardOptions = useMemo(() => {
    const setVals = new Set<string>();
    data.forEach((row: any) => {
      const funcs = String(row.function || '')
        .split(',')
        .map((s: string) => s.trim())
        .filter(Boolean);
      funcs.forEach((f: string) => setVals.add(f));
    });
    return Array.from(setVals).sort();
  }, [data]);

  // Apply client-side Standard filter for rendering and export if needed
  const filteredData = useMemo(() => {
    if (!standardFilter) return data;
    return data.filter((row: any) => String(row.function || '').split(',').map((s: string) => s.trim()).includes(standardFilter));
  }, [data, standardFilter]);

  // Update last search count after data/filter change when a search term was submitted
  useEffect(() => {
    if (lastSearchTerm !== null) {
      setLastSearchCount(filteredData.length);
    }
  }, [filteredData, lastSearchTerm]);

  // Control References (Standards) from API and selected standardId
  const [controlReferences, setControlReferences] = useState<any[]>([]);
  const [selectedStandardId, setSelectedStandardId] = useState<string | null>(null);

  const hasRcsaFilters = useMemo(
    () =>
      Boolean(selectedStandardId) ||
      selectedQuarter !== "Q1" ||
      selectedYear !== new Date().getFullYear(),
    [selectedStandardId, selectedQuarter, selectedYear],
  );

  const fetchControlReferences = async () => {
    try {
      const res: any = await getReferences({ query: '', perPage: 1000, pageNo: 0 });
      // API returns { success, data } where data could be an array
      const items = res?.data || res; // fallback if service returns array directly
      if (Array.isArray(items)) {
        setControlReferences(items);
      }
    } catch (e) {
      console.error('Error fetching control references:', e);
    }
  };

  // Options for quarter and year
  const quarterOptions = [
    { value: 'Q1', label: 'Q1' },
    { value: 'Q2', label: 'Q2' },
    { value: 'Q3', label: 'Q3' },
    { value: 'Q4', label: 'Q4' },
  ];

  const yearOptions: any = Array.from({ length: 10 }, (_, i) => {
    const year = new Date().getFullYear() - i;
    return { value: year, label: year.toString() };
  });

  // Bank response choices for edit modal - will be populated dynamically from API
  const [bankResponseChoices, setBankResponseChoices] = useState<string[]>([]);

  // Helper function to truncate long text with tooltip
  const renderTruncatedText = (text: string, maxLength: number = 100) => {
    if (!text || text.length <= maxLength) {
      return <span>{text}</span>;
    }

    const tooltipId = `tooltip-${Math.random().toString(36).substr(2, 9)}`;

    return (
      <div className="truncated-text-container">
        <span className="truncated-text">{text.substring(0, 80)}...</span>
        <span 
          id={tooltipId}
          className="tooltip-trigger"
          title={text}
        >
          <ThreeDots width={20} height={20} />
        </span>
        <Tooltip
          target={tooltipId}
          placement="top"
          className="text-left"
          style={{
            backgroundColor: '#333',
            color: 'white',
            maxWidth: '400px',
            fontSize: '12px',
            padding: '8px 12px',
            borderRadius: '6px',
            zIndex: 9999
          }}
        >
          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {text}
          </div>
        </Tooltip>
      </div>
    );
  };

  // Helper function to copy control text
  const copyControlText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t("Copied") || "Copied");
    } catch (e) {
      console.error("Clipboard copy failed", e);
      toast.error(t("Copy failed") || "Copy failed");
    }
  };

  // Helper function to render clickable controls
  const renderClickableControls = (item: any) => {
    // Prefer parsing from the raw item controls JSON when available
    let parsedControls: any[] = [];
    const rawControls = item?.rawItem?.controls;

    if (rawControls) {
      try {
        const controlsJson = safeParseJSON(rawControls);
        if (Array.isArray(controlsJson)) {
          parsedControls = controlsJson;
        }
      } catch (e) {
        // Fall back below
      }
    }

    // Fallback: show non-clickable labels from the aggregated mapping string
    if (!parsedControls.length) {
      if (!item?.controlMapping) return <span>N/A</span>;
      const labels = String(item.controlMapping)
        .split("\n")
        .map(s => s.trim())
        .filter(Boolean);
      if (!labels.length) return <span>N/A</span>;
      return (
        <div className="controls-container">
          {labels.map((label: string, idx: number) => (
            <div
              key={idx}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                marginRight: '4px',
                marginBottom: '2px',
                maxWidth: '100%'
              }}
            >
              <span
                className="control-item clickable"
                style={{
                  cursor: 'default',
                  color: '#007bff',
                  textDecoration: 'underline',
                  display: 'inline-block',
                  padding: '2px 4px',
                  borderRadius: '4px',
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  transition: 'all 0.2s ease',
                  wordWrap: 'break-word',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  marginRight: '2px'
                }}
              >
                {label}
              </span>
              <button
                onClick={() => copyControlText(label)}
                style={{
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  padding: '1px 4px',
                  fontSize: '8px',
                  cursor: 'pointer',
                  opacity: 0.8,
                  transition: 'opacity 0.2s ease',
                  minWidth: '16px',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '0.8';
                }}
                title={t("Copy this control") || "Copy this control"}
              >
                📋
              </button>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="controls-container">
        {parsedControls.map((control: any, index: number) => {
          const controlId = control?.id;
          const complianceId = item?.rawItem?.surveyAssessmentId || item?.rawItem?.id || item?.rawItem?.surveyAssessment?.id;
          const domainId = control?.domains?.[0]?.id;

          const isClickable = Boolean(controlId && complianceId && domainId);
          const handleControlClick = () => {
            if (!isClickable) return;
            const targetUrl = `/compliances/view-control/${controlId}/${complianceId}/${domainId}`;
            navigate(targetUrl);
          };

          const label = control?.description || 'N/A';

          return (
            <div
              key={index}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                marginRight: '4px',
                marginBottom: '2px',
                maxWidth: '100%'
              }}
            >
              <span
                className={`control-item ${isClickable ? 'clickable' : 'disabled'}`}
                onClick={handleControlClick}
                title={isClickable ? 'View control' : 'Missing metadata to navigate'}
                style={{
                  cursor: isClickable ? 'pointer' : 'not-allowed',
                  color: '#007bff',
                  textDecoration: 'underline',
                  display: 'inline-block',
                  padding: '2px 4px',
                  borderRadius: '4px',
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  transition: 'all 0.2s ease',
                  wordWrap: 'break-word',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  opacity: isClickable ? 1 : 0.6,
                  marginRight: '2px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#e9ecef';
                  e.currentTarget.style.borderColor = '#007bff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                  e.currentTarget.style.borderColor = '#dee2e6';
                }}
              >
                {label}
              </span>
              <button
                onClick={() => copyControlText(label)}
                style={{
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  padding: '1px 4px',
                  fontSize: '8px',
                  cursor: 'pointer',
                  opacity: 0.8,
                  transition: 'opacity 0.2s ease',
                  minWidth: '16px',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '0.8';
                }}
                title={t("Copy this control") || "Copy this control"}
              >
                📋
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  // Function to fetch impacted areas
  const fetchImpactedAreas = async () => {
    try {
      const data: any = await fetchDataApi(`impacted-areas?perPage=1000&pageNo=0&query=`);
      if (data.success) {
        setImpactedAreas(data.data);
      }
    } catch (error: any) {
      console.error('Error fetching impacted areas:', error);
    }
  };

  // Convert impacted areas to select options
  const impactedAreaOptions = impactedAreas.map((area: any) => ({
    value: area.id,
    label: area.name
  }));

  const exportToExcel = async () => {
      setLoading(true);
    
      try {
        // Use the last submitted search term if available; fallback to current input
        const exportQuery = normalizeQuery((lastSearchTerm !== null ? lastSearchTerm : searchTerm).trim());
        // Fetch up to 500 rows from the same API for export
        const exportResp = await getRCSAByPage(
          selectedQuarter,
          selectedYear,
          0,
          500
        );

        let dataToExport: any[] = [];
        if (exportResp?.success) {
          const rawData = exportResp?.data?.data || [];
          // Process rows similarly to fetchRCSAData mapping
          dataToExport = rawData
            .map((item: any, index: number) => {
              if (!item?.question_en) return null;

              let questionText = item.question_en || "N/A";
              let controlMappingSet = new Set<string>();
              let evidenceSet = new Set<string>();
              let complianceBriefSet = new Set<string>();
              let impactedAreaSet = new Set<string>();
              let functionSetItem = new Set<string>();
              let domainSetItem = new Set<string>();
              let ownerSet = new Set<string>();
              let controlType = "N/A";

              if (item?.controls) {
                try {
                  const controls = safeParseJSON(item.controls);
                  if (Array.isArray(controls)) {
                    controls.forEach((control: any) => {
                      if (control?.domains?.length > 0) {
                        control.domains.forEach((d: any) => {
                          const funcName = d.parentName || "N/A";
                          const domainName = d.name || "N/A";
                          if (funcName !== "N/A") functionSetItem.add(funcName);
                          if (domainName !== "N/A") domainSetItem.add(domainName);
                        });
                      }
                      if (control?.description && control.description !== "N/A") {
                        controlMappingSet.add(control.description);
                      }
                      controlType =
                        control?.keyControl === true
                          ? "Mandatory"
                          : control?.keyControl === false
                          ? "Advisory"
                          : "N/A";
                      if (control?.complianceActions?.length > 0) {
                        control.complianceActions.forEach((action: any) => {
                          if (action?.evidence && action.evidence !== "N/A") {
                            evidenceSet.add(action.evidence);
                          }
                          if (action?.justification && action.justification !== "N/A") {
                            complianceBriefSet.add(action.justification);
                          }
                          if (action?.compliance?.compliance) {
                            ownerSet.add(action.compliance.compliance);
                          }
                          if (action?.impactedAreas?.length > 0) {
                            action.impactedAreas.forEach((ia: any) => {
                              if (ia?.name) impactedAreaSet.add(ia.name);
                            });
                          }
                        });
                      }
                    });
                  }
                } catch {}
              }

              if (item?.answeredBy) {
                try {
                  const answeredBy = JSON.parse(item.answeredBy);
                  const answeredByName = answeredBy?.name || "N/A";
                  if (answeredByName !== "N/A") ownerSet.add(answeredByName);
                } catch {}
              }

              return {
                index: index + 1,
                function: Array.from(functionSetItem).join(", ") || "N/A",
                domain: Array.from(domainSetItem).join(", ") || "N/A",
                question: questionText,
                bankResponse: item?.answer || "N/A",
                controlType,
                examples: Array.from(evidenceSet).join("\n") || "N/A",
                controlMapping: Array.from(controlMappingSet).join("\n") || "N/A",
                complianceBrief: Array.from(complianceBriefSet).join("\n") || "N/A",
                impactedArea: Array.from(impactedAreaSet).join(", ") || "N/A",
                owner: Array.from(ownerSet).join(", ") || "N/A",
              };
            })
            .filter(Boolean) as any[];

          // Apply Standard (Function) filter to export if selected
          if (standardFilter) {
            dataToExport = dataToExport.filter((row: any) => String(row.function || '').split(',').map((s: string) => s.trim()).includes(standardFilter));
          }
        } else {
          // Fallback to currently displayed data if API fails
          dataToExport = (standardFilter
            ? data.filter((row: any) => String(row.function || '').split(',').map((s: string) => s.trim()).includes(standardFilter))
            : data);
        }
        
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Bank's Self Assessment");
    
        // Use processed data from API
    
        // Add headers
        worksheet.columns = [
          { header: '#', key: 'index', width: 8 },
          { header: t("Function") as string, key: "function", width: 30 },
          { header: t("Domain") as string, key: "domain", width: 30 },
          { header: t("Question") as string, key: "question", width: 60 },
          { header: t("Bank Response") as string, key: "bankResponse", width: 20 },
          { header: t("Control Type") as string, key: "controlType", width: 15 },
          { header: t("Examples of Required Samples") as string, key: "examples", width: 40 },
          { header: t("Control Mapping") as string, key: "controlMapping", width: 50 },
          { header: t("Bank Compliance Brief / Justification") as string, key: "complianceBrief", width: 50 },
          { header: t("Impacted/Affected Area") as string, key: "impactedArea", width: 30 },
          { header: t("Owner") as string, key: "owner", width: 25 },
        ];
    
        // Style header
        const headerRow = worksheet.getRow(1);
        headerRow.height = 30;
        headerRow.eachCell((cell) => {
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "000000" } };
          cell.font = { color: { argb: "FFFFFF" }, bold: true };
          cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
          cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
        });
    
        // Add data rows
        dataToExport.forEach((row: any) => {
          const newRow = worksheet.addRow(row);
 
          // Index Column
          const indexCell = newRow.getCell('index');
          indexCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7EB' } }; // light gray
          indexCell.font = { color: { argb: 'FF000000' } };
          indexCell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
          indexCell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };

          // Function Column
          const functionCell = newRow.getCell("function");
          functionCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD0B162" } };
          functionCell.font = { color: { argb: "000000" } };
          functionCell.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
          functionCell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
    
          // Domain Column
          const domainCell = newRow.getCell("domain");
          domainCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF2E9D2" } };
          domainCell.font = { color: { argb: "000000" } };
          domainCell.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
          domainCell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
    
          // Control Type Column
          const controlTypeCell = newRow.getCell("controlType");
          const mandatoryColor = "FFDC4A4A";
          const advisoryColor = "FFE8C412";
          const defaultColor = "FFFFFFFF";
          controlTypeCell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: row.controlType === "Mandatory" ? mandatoryColor : row.controlType === "Advisory" ? advisoryColor : defaultColor },
          };
          controlTypeCell.font = {
            color: { argb: row.controlType === "Advisory" ? "FF000000" : "FFFFFFFF" },
          };
          controlTypeCell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
          controlTypeCell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
    
          // Wrap text for long columns
          ["question", "examples", "controlMapping", "complianceBrief", "impactedArea"].forEach((key) => {
            const cell = newRow.getCell(key);
            cell.alignment = { wrapText: true, vertical: "middle", horizontal: "left" };
          });
        });
    
        // Save workbook
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/octet-stream" });
        saveAs(blob, "Bank's Self Assessment.xlsx");
      } catch (err) {
        console.error("Error exporting Excel:", err);
        toast.error("Failed to export Excel file");
      } finally {
        setLoading(false);
      }
  };
    


  const customSelectStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      minHeight: 40,
      borderRadius: 8,
      borderColor: state.isFocused ? "rgba(31, 124, 80, 0.55)" : "rgba(31, 124, 80, 0.35)",
      boxShadow: "none",
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      padding: "0 8px",
    }),
    input: (provided: any) => ({
      ...provided,
      margin: "0px",
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      height: 38,
    }),
    menuPortal: (base: any) => ({ ...base, zIndex: 1070 }),
  };

  const catalogTotalPages = Math.max(1, totalPages || 1);
  const rangeEnd = filteredData.length === 0 ? 0 : page * pageSize + filteredData.length;
  const displayTotalForMeta = totalCount > 0 ? totalCount : rangeEnd;
  const totalFiltered = totalCount > 0 ? totalCount : rangeEnd;

  const handlePagination = (newPage: number) => {
    if (newPage >= 0 && (totalPages <= 0 || newPage < totalPages)) {
      setPageNo(newPage);
      fetchRCSAData(newPage, pageSize, normalizeQuery(searchTerm), selectedStandardId || undefined, {
        quarter: selectedQuarter,
        year: selectedYear,
      });
    }
  };

  const handleCatalogPageChange = (nextOneBased: number) => {
    handlePagination(nextOneBased - 1);
  };

  // Edit modal functions
  const openEditModal = (item: any) => {
    if (!item) {
      toast.error("Invalid item selected");
      return;
    }
    
    // Extract choices from the raw item's choices property
    if (item.rawItem?.choices) {
      const choicesArray = item.rawItem.choices.split(',').map((choice: string) => choice.trim());
      setBankResponseChoices(choicesArray);
    } else {
      // Fallback to default choices if none found
      setBankResponseChoices(["Yes", "No", "Partially Implemented", "Not Applicable"]);
    }
    
    setEditingItem(item);
    setSelectedResponse(item.bankResponse || "");
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingItem(null);
    setSelectedResponse("");
  };

  const handleResponseChange = (response: string) => {
    setSelectedResponse(response);
  };

  const openViewModal = (title: string, content: string) => {
    setViewModalTitle(title);
    setViewModalContent(content || "");
    setIsViewModalOpen(true);
  };

  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setViewModalTitle("");
    setViewModalContent("");
  };

  const copyViewContent = async () => {
    try {
      await navigator.clipboard.writeText(viewModalContent);
      toast.success(t("Copied") || "Copied");
    } catch (e) {
      console.error("Clipboard copy failed", e);
      toast.error(t("Copy failed") || "Copy failed");
    }
  };

 const handleUpdateResponse = async () => {
  if (!editingItem || !selectedResponse) {
    toast.error("Please select a response");
    return;
  }

  // Check permissions before proceeding
  if (!canUpdate && !isSuperAdmin) {
    toast.error("You don't have permission to update bank responses");
    return;
  }

  // Store current page before update
  const currentPageBeforeUpdate = page;

  setIsUpdating(true);
  try {
    // Find the raw item to get the survey assessment ID
    const rawItem = editingItem.rawItem;
    
    if (!rawItem) {
      toast.error("Survey assessment data not found");
      return;
    }

    const surveyAssessmentId = rawItem.id || rawItem.surveyAssessmentId || rawItem.surveyAssessment?.id;
    
    if (!surveyAssessmentId) {
      toast.error("Survey assessment ID not found");
      return;
    }

    const updateData = {
      answer: selectedResponse
    };

    // Optimistic update
    const previousData = data;
    const optimisticallyUpdated = data.map(item =>
      item.index === editingItem.index 
        ? { ...item, bankResponse: selectedResponse }
        : item
    );
    setData(optimisticallyUpdated);
    // Close modal immediately for responsive UX
    closeEditModal();

    const response = await updateSurveyAssessmentApi(surveyAssessmentId, updateData);
    
    if (response.success) {
      toast.success("Bank response updated successfully");
    } else {
      // Rollback on failure
      setData(previousData);
      toast.error(response.message || "Failed to update bank response");
    }
  } catch (error: any) {
    console.error("Error updating bank response:", error);
    // Rollback on error
    setData(prev => prev.map(item =>
      item.index === editingItem?.index ? { ...item, bankResponse: editingItem?.bankResponse } : item
    ));
    toast.error(error.message || "An error occurred while updating");
  } finally {
    setIsUpdating(false);
  }
};

  const fetchUserRolePermissions = async () => {
    try {
      const user = await getUserInfo();
      const userId = user?.id || "";
      const permissions = await getUserRolePermissions(userId);
     
      if (permissions.success) {
        // Backend may return page as "surveys", "survey", or legacy "RCSA"
        const rcsaPageAliases = new Set(['surveys', 'survey', 'rcsa']);
        const userPagePermission = permissions.data.permissions.find((permission: any) => {
          const page = String(permission?.page || '').trim().toLowerCase();
          return rcsaPageAliases.has(page);
        });

        if (userPagePermission) {
          setUserPermissions(userPagePermission);
          setNotAllowed(!userPagePermission.show);
          // Check update permission
          setCanUpdate(userPagePermission.edit || false);
          
          // Check if user is Super Admin
          const userRole = getUserRole();
          setIsSuperAdmin(userRole === 'super_admin_');
        } else {
          setNotAllowed(true);
        }
      } else {
        setNotAllowed(true);
      }
    } catch (error: any) {
      toast.error(
        `${t("permissions.Error_fetching_user_role_permissions")}${error?.message ? `: ${error.message}` : ''}`
      );
    }
  };


  function safeParseJSON(jsonString: string) {
    try {
      return JSON.parse(jsonString);
    } catch (e:any) {
      console.error("First parse failed:", e.message);
  
      // Attempt cleaning
      let fixed = jsonString
        .replace(/[\r\n\t]/g, " ") // remove line breaks and tabs
        .replace(/'/g, '"') // convert single quotes to double
        .replace(/,\s*}/g, "}") // remove trailing commas in objects
        .replace(/,\s*]/g, "]"); // remove trailing commas in arrays
  
      try {
        return JSON.parse(fixed);
      } catch (e2:any) {
        console.error("Second parse failed:", e2.message);
        console.error("Problematic string:", fixed);
        return null; // fallback
      }
    }
  }
  




  const fetchRCSAData = async (
    pageNum: number = 0,
    pageSizeNum: number = pageSize,
    searchQuery: string = "",
    controlReferenceId?: string,
    periodOverride?: { quarter: string; year: number } | null,
  ) => {
    const quarter = periodOverride?.quarter ?? selectedQuarter;
    const year = periodOverride?.year ?? selectedYear;
    // Cancel any in-flight request
    try {
      fetchControllerRef.current?.abort();
    } catch {}

    const controller = new AbortController();
    fetchControllerRef.current = controller;

    const currentFetchId = ++latestFetchIdRef.current;

    setLoading(true);
    setSearchLoading(!!searchQuery);
  
    try {
      const response = await getRCSAByPage(quarter, year, pageNum, pageSizeNum);
      
      // If a newer request has been fired or this one was aborted, ignore
      if (currentFetchId !== latestFetchIdRef.current || controller.signal.aborted) {
        return;
      }

      if (response.success) {
        const rawData = response?.data?.data || [];
        const totalPages = response?.data?.totalPages || 0;
        const totalCountResp = response?.data?.total || 0;
  
        const functionSet = new Set<string>();
  
        const processedData = rawData
          .map((item: any, index: number) => {
            if (!item?.question_en) return null;
  
            let questionText = item.question_en || "N/A";
            let controlMappingSet = new Set<string>();
            let evidenceSet = new Set<string>();
            let complianceBriefSet = new Set<string>();
            let impactedAreaSet = new Set<string>();
            let functionSetItem = new Set<string>();
            let domainSetItem = new Set<string>();
            let ownerSet = new Set<string>();
            let controlType = "N/A";
  
            // Parse controls
            if (item?.controls) {
              try {
                const controls = safeParseJSON(item.controls);
  
                if (Array.isArray(controls)) {
                  controls.forEach((control: any) => {
                    // Domains and function
                    if (control?.domains?.length > 0) {
                      control.domains.forEach((d: any) => {
                        const funcName = d.parentName || "N/A";
                        const domainName = d.name || "N/A";
                        if (funcName !== "N/A") {
                          functionSetItem.add(funcName);
                          functionSet.add(funcName); // global set
                        }
                        if (domainName !== "N/A") {
                          domainSetItem.add(domainName);
                        }
                      });
                    }
  
                    // Control description
                    if (control?.description && control.description !== "N/A") {
                      controlMappingSet.add(control.description);
                    }
  
                    // Control type
                    controlType =
                      control?.keyControl === true
                        ? "Mandatory"
                        : control?.keyControl === false
                        ? "Advisory"
                        : "N/A";
  
                    // Compliance Actions
                    if (control?.complianceActions?.length > 0) {
                      control.complianceActions.forEach((action: any) => {
                        if (action?.evidence && action.evidence !== "N/A") {
                          evidenceSet.add(action.evidence);
                        }
                        if (action?.justification && action.justification !== "N/A") {
                          complianceBriefSet.add(action.justification);
                        }
                        if (action?.compliance?.compliance) {
                          ownerSet.add(action.compliance.compliance);
                        }
                        if (action?.impactedAreas?.length > 0) {
                          action.impactedAreas.forEach((ia: any) => {
                            if (ia?.name) impactedAreaSet.add(ia.name);
                          });
                        }
                      });
                    }
                  });
                }
              } catch (err) {
                console.error("Error parsing controls:", err);
              }
            }
  
            // Parse answeredBy
            let answeredByName = "N/A";
            if (item?.answeredBy) {
              try {
                const answeredBy = JSON.parse(item.answeredBy);
                answeredByName = answeredBy?.name || "N/A";
                if (answeredByName !== "N/A") ownerSet.add(answeredByName);
              } catch (err) {
                console.error("Error parsing answeredBy:", err);
              }
            }
  
            return {
              index: pageNum * pageSizeNum + index + 1,
              function: Array.from(functionSetItem).join(", ") || "N/A",
              domain: Array.from(domainSetItem).join(", ") || "N/A",
              question: questionText,
              bankResponse: item?.answer || "N/A",
              controlType,
              examples: Array.from(evidenceSet).join("\n") || "N/A",
              controlMapping: Array.from(controlMappingSet).join("\n") || "N/A",
              complianceBrief: Array.from(complianceBriefSet).join("\n") || "N/A",
              impactedArea: Array.from(impactedAreaSet).join(", ") || "N/A",
              owner: Array.from(ownerSet).join(", ") || "N/A",
              rawItem: item,
            };
          })
          .filter(Boolean);
  
        setData(processedData);
        setTotalPages(totalPages);
        setTotalCount(totalCountResp);
  
       // console.log("Unique Functions:", Array.from(functionSet));
      } else {
        toast.error(rcsaErrorToastMessage(response.message));
        setData([]);
        setTotalPages(0);
        setTotalCount(0);
      }
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        // Silently ignore aborted requests
        return;
      }
      console.error("Error fetching RCSA data:", error);
      toast.error(rcsaErrorToastMessage(error?.message));
      setData([]);
      setTotalPages(0);
      setTotalCount(0);
    } finally {
      if (currentFetchId === latestFetchIdRef.current) {
        setLoading(false);
        setSearchLoading(false);
      }
    }
  };
  


  useEffect(() => {
    const fetchUserData = async () => {
      try {
        await fetchUserRolePermissions();
        await fetchImpactedAreas();
        await fetchControlReferences();
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
  
    fetchUserData();
  }, []);

  // Initial load (quarter/year/standard are applied via filter popover or inherited defaults)
  useEffect(() => {
    fetchRCSAData(0, pageSize, "", undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateFilterAnchor = useCallback(() => {
    if (filterTriggerRef.current) {
      const r = filterTriggerRef.current.getBoundingClientRect();
      setFilterAnchor({ top: r.bottom, left: r.left, right: r.right });
    }
  }, []);

  useLayoutEffect(() => {
    if (filterPopupOpen) updateFilterAnchor();
    else setFilterAnchor(null);
  }, [filterPopupOpen, updateFilterAnchor]);

  useEffect(() => {
    if (!filterPopupOpen) return;
    const fn = () => updateFilterAnchor();
    window.addEventListener("scroll", fn, true);
    window.addEventListener("resize", fn);
    return () => {
      window.removeEventListener("scroll", fn, true);
      window.removeEventListener("resize", fn);
    };
  }, [filterPopupOpen, updateFilterAnchor]);

  const filterPopupStyle = useMemo(
    () => (filterAnchor ? getRcsaFilterPopupStyle(filterAnchor, isRTL) : undefined),
    [filterAnchor, isRTL],
  );

  const openFilterPanel = () => {
    setFilterDraftQuarter(selectedQuarter);
    setFilterDraftYear(selectedYear);
    setFilterDraftStandardId(selectedStandardId);
    setFilterPopupOpen(true);
  };

  const applyFilterPanel = async () => {
    setSelectedQuarter(filterDraftQuarter);
    setSelectedYear(filterDraftYear);
    setSelectedStandardId(filterDraftStandardId);
    setPageNo(0);
    await fetchRCSAData(0, pageSize, normalizeQuery(searchTerm), filterDraftStandardId || undefined, {
      quarter: filterDraftQuarter,
      year: filterDraftYear,
    });
    setFilterPopupOpen(false);
  };

  // Disabled auto-search to avoid multiple calls; manual trigger via Enter/button
  useEffect(() => {
    // Keep for typing indicator only
  }, [debouncedSearchTerm]);

  const handleSearchSubmit = async () => {
    setPageNo(0);
    const normalized = normalizeQuery(searchTerm);
    await fetchRCSAData(0, pageSize, normalized, selectedStandardId || undefined, {
      quarter: selectedQuarter,
      year: selectedYear,
    });
    setLastSearchTerm(normalized);
  };

  const handleSmartSearch = (searchValue: string) => {
    setSearchTerm(searchValue);
  };

  const handleDownloadTemplate = () => {
    const rcsaData = [
      [
        "Survey Name", 
        "Category", 
        "Reference", 
        "Question", 
        "Control Mapping", 
        "Bank Response", 
        "Control Type",
        "Quarter", 
        "Year", 
        "Due Date", 
        "Domain", 
        "Bank Compliance Brief Description/Justifications", 
        "Manager", 
        "Implementer", 
        "Impacted/Affected Area", 
        "answerdby"
      ],
      [
        "Survey 1", 
        "Category 1", 
        "Reference 1", 
        "Question 1", 
        "Control 1", 
        "Fully Implemented", 
        "Mandatory",
        "Q1", 
        "2024", 
        "2024-03-31", 
        "Domain 1", 
        "Description 1", 
        "Manager Name", 
        "Implementer Name", 
        "Area 1, Area 2", 
        "User Name"
      ],
    ];
  
    const workbook = XLSX.utils.book_new();
    const rcsaSheet = XLSX.utils.aoa_to_sheet(rcsaData);
    XLSX.utils.book_append_sheet(workbook, rcsaSheet, 'RCSA');
    XLSX.writeFile(workbook, "Bank's Self Assessment.xlsx");
  };
  
  const handleImportExcel = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
        setLoading(true);
        try {
            const response = await uploadExcelForRCSA(file);
            toast.success(response.message || "Excel file imported successfully");
            await fetchRCSAData(0, pageSize, normalizeQuery(searchTerm), selectedStandardId || undefined, {
              quarter: selectedQuarter,
              year: selectedYear,
            });
        } catch (error) {
            toast.error("Error importing Excel file");
        }
        setLoading(false);
    }
    event.target.value = "";
  };

  const busyOverlay = loading || searchLoading;

  return (
    <div
      className="root container-fluid rcsa-compliance-catalog-page document-management-container document-management-page-wrap services-catalog-page data-mgmt-categories-page p-3 controls-catalog-main services-catalog-page-inner"
      style={{ zoom: 0.9 }}
    >
      {busyOverlay && (
        <div className="loading-overlay">
          <div className="spinner" />
          <span>{t("common.loading", "Loading...")}</span>
        </div>
      )}

      {!notAllowed && (
        <>
          <div className="services-catalog-page-header card border-0 shadow-sm mb-2">
            <div className="card-body py-2 px-3 px-lg-4">
              <div className="d-flex flex-column flex-lg-row align-items-stretch align-items-lg-center justify-content-lg-between gap-3 w-100 controls-catalog-header-actions">
                <div className="flex-grow-1 controls-catalog-search-plain" style={{ minWidth: 0 }}>
                  <input
                    id="rcsa-compliance-search"
                    type="search"
                    className="form-control form-control-sm border bg-white"
                    value={searchTerm}
                    onChange={(e) => handleSmartSearch(e.target.value.replace(/^\s+/, ""))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleSearchSubmit();
                      }
                    }}
                    placeholder={String(t("Search...") || "Search...")}
                    autoComplete="off"
                    aria-label={String(t("Search...") || "Search")}
                  />
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-center justify-content-lg-end flex-shrink-0 services-catalog-primary-actions">
                  {(userPermissions?.create || isSuperAdmin) && (
                    <>
                      <input
                        type="file"
                        accept=".xlsx, .xls"
                        onChange={handleImportExcel}
                        style={{ display: "none" }}
                        id="import-excel-rcsa-compliance"
                      />
                      <button
                        type="button"
                        className="btn btn-sm surveys-page__btn surveys-page__btn--outline d-inline-flex align-items-center justify-content-center gap-1"
                        onClick={handleDownloadTemplate}
                      >
                        <MdOutlineDownload size={16} aria-hidden />
                        {t("References_Page.download_excel_template")}
                      </button>
                      <label
                        htmlFor="import-excel-rcsa-compliance"
                        className="btn btn-sm surveys-page__btn surveys-page__btn--import-soft d-inline-flex align-items-center justify-content-center gap-1 mb-0"
                        style={{ cursor: "pointer" }}
                      >
                        <MdOutlineUpload size={16} aria-hidden />
                        {t("References_Page.import_excel")}
                      </label>
                    </>
                  )}
                  <button
                    type="button"
                    className="btn btn-sm surveys-page__btn surveys-page__btn--primary d-inline-flex align-items-center justify-content-center gap-1"
                    onClick={exportToExcel}
                    disabled={loading}
                  >
                    <CiExport size={18} aria-hidden />
                    {t("Export to Excel") || "Export to Excel"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {typeof document !== "undefined" &&
            createPortal(
              <>
                {filterPopupOpen && filterAnchor && filterPopupStyle && (
                  <div
                    className={`controls-catalog-popup-panel rcsa-compliance-filter-popover question-catalog-popup-layer ${isRTL ? "controls-catalog-popup-panel-rtl" : ""}`}
                    style={{ ...filterPopupStyle, zIndex: 1060 }}
                    role="dialog"
                    aria-label={String(t("common.filters", { defaultValue: "Filters" }))}
                  >
                    <div className="controls-catalog-popup-header">
                      <span className="controls-catalog-popup-title">
                        {t("common.filters", { defaultValue: "Filters" })}
                      </span>
                      <button
                        type="button"
                        className="controls-catalog-popup-close"
                        aria-label={String(t("common.close", { defaultValue: "Close" }))}
                        onClick={() => setFilterPopupOpen(false)}
                      >
                        &times;
                      </button>
                    </div>
                    <div className={`controls-catalog-popup-body ${isRTL ? "text-end" : "text-start"}`}>
                    <div className="d-flex flex-column gap-3">
                      <div>
                        <label
                          className="form-label small mb-0 text-muted fw-semibold"
                          style={{ fontSize: "0.75rem" }}
                          htmlFor="rcsa-filter-quarter-pop"
                        >
                          {t("Quarter", "Quarter")}
                        </label>
                        <Select
                          inputId="rcsa-filter-quarter-pop"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          options={quarterOptions}
                          styles={customSelectStyles}
                          placeholder="Select Quarter"
                          value={quarterOptions.find((option: any) => option.value === filterDraftQuarter)}
                          onChange={(option: any) => setFilterDraftQuarter(option?.value || "Q1")}
                        />
                      </div>
                      <div>
                        <label
                          className="form-label small mb-0 text-muted fw-semibold"
                          style={{ fontSize: "0.75rem" }}
                          htmlFor="rcsa-filter-year-pop"
                        >
                          {t("Year", "Year")}
                        </label>
                        <Select
                          inputId="rcsa-filter-year-pop"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          options={yearOptions}
                          styles={customSelectStyles}
                          placeholder="Select Year"
                          value={yearOptions.find((option: any) => option.value === filterDraftYear)}
                          onChange={(option: any) =>
                            setFilterDraftYear(option?.value ?? new Date().getFullYear())
                          }
                        />
                      </div>
                      <div>
                        <label
                          className="form-label small mb-0 text-muted fw-semibold"
                          style={{ fontSize: "0.75rem" }}
                          htmlFor="rcsa-filter-standard-pop"
                        >
                          {t("Filter by Control Reference", "Filter by Control Reference")}
                        </label>
                        <Select
                          inputId="rcsa-filter-standard-pop"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          options={[
                            { value: "", label: t("All Standard") || "All Standard" },
                            ...(controlReferences || []).map((cr: any) => ({ value: cr.id, label: cr.name })),
                          ]}
                          styles={customSelectStyles}
                          placeholder={t("Filter by Control Reference") || "Filter by Control Reference"}
                          value={(() => {
                            if (!filterDraftStandardId) {
                              return { value: "", label: t("All Standards") || "All Standards" };
                            }
                            const found = (controlReferences || []).find(
                              (cr: any) => cr.id === filterDraftStandardId,
                            );
                            return {
                              value: filterDraftStandardId,
                              label: found?.name || filterDraftStandardId,
                            };
                          })()}
                          onChange={(option: any) => setFilterDraftStandardId(option?.value || null)}
                        />
                      </div>
                    </div>
                    </div>
                    <div className={`controls-catalog-popup-footer ${isRTL ? "flex-row-reverse" : ""}`}>
                      <button type="button" className="btn btn-primary btn-sm" onClick={applyFilterPanel}>
                        {t("common.apply", { defaultValue: "Apply" })}
                      </button>
                    </div>
                  </div>
                )}
              </>,
              document.body,
            )}

          {lastSearchTerm !== null && (
            <div
              className="rounded mb-2 px-3 py-2 small rcsa-compliance-page__search-banner"
              style={{
                border: "1px solid rgba(26, 92, 61, 0.2)",
                background: "#fafbfc",
                color: "#374151",
              }}
            >
              {searchLoading ? (
                <span>{t("common.loading", "Searching...")}</span>
              ) : (
                <span>
                  {t("Found")} {lastSearchCount} {t("of")} {totalCount}{" "}
                  {t("questions matching")} &quot;{lastSearchTerm}&quot;
                </span>
              )}
            </div>
          )}

          <div className="controls-catalog-tools-card services-catalog-table-toolbar card border-0 shadow-sm mb-2">
            <div className="card-body py-2 px-3 px-lg-4">
              <div className="d-flex flex-column flex-md-row flex-wrap align-items-stretch align-items-md-center justify-content-between gap-3 services-catalog-toolbar-row controls-catalog-tools-row">
                <div className="d-flex flex-wrap align-items-center gap-3 gap-md-4">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span
                      className="small text-muted fw-semibold mb-0 text-uppercase"
                      style={{ fontSize: "0.7rem", letterSpacing: "0.04em" }}
                    >
                      {t("assets.totalRecords", { defaultValue: "Total records" })}
                    </span>
                    <span className="fw-semibold text-dark">{displayTotalForMeta}</span>
                  </div>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <label
                      className="small text-muted fw-semibold mb-0 text-uppercase"
                      style={{ fontSize: "0.7rem", letterSpacing: "0.04em" }}
                      htmlFor="rcsa-toolbar-page-size"
                    >
                      {t("assets.pageSize", { defaultValue: "Rows per page" })}
                    </label>
                    <select
                      id="rcsa-toolbar-page-size"
                      className="form-select form-select-sm services-catalog-toolbar-select"
                      style={{ width: "auto", minWidth: "4.5rem", maxWidth: "6rem" }}
                      value={pageSize}
                      onChange={(e) => {
                        const n = Number(e.target.value);
                        setPageSize(n);
                        setPageNo(0);
                        fetchRCSAData(0, n, normalizeQuery(searchTerm), selectedStandardId || undefined, {
                          quarter: selectedQuarter,
                          year: selectedYear,
                        });
                      }}
                      aria-label={String(t("document_categories.rowsPerPage", "Rows per page"))}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={30}>30</option>
                    </select>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-md-end gap-2 controls-catalog-toolbar-icons ms-md-auto">
                  <div
                    className={`controls-catalog-tab-pair ${filterPopupOpen ? "controls-catalog-tab-pair--open" : ""} ${hasRcsaFilters ? "controls-catalog-tab-pair--has-filters" : ""}`}
                    role="group"
                    aria-label={String(t("common.filters", { defaultValue: "Filters" }))}
                  >
                    <span className="controls-catalog-tab-pair__first">
                      {String(t("controls.catalogViewTab", { defaultValue: "List" }))}
                    </span>
                    <button
                      ref={filterTriggerRef}
                      type="button"
                      className="controls-catalog-tab-pair__filter"
                      onClick={() => {
                        if (!filterPopupOpen) openFilterPanel();
                      }}
                      aria-label={String(t("controls.filter", { defaultValue: "Filter" }))}
                      aria-expanded={filterPopupOpen}
                      aria-pressed={filterPopupOpen}
                    >
                      <FilterIcon size={18} aria-hidden />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="table-responsive position-relative mb-0">
          <table className="table table-bordered table-hover services-catalog-table mb-0 rcsa-compliance-catalog-table">
            <thead>
              <tr>
                <th style={{ width: "5%" }} className="align-middle text-center">#</th>
                <th className="align-middle">{t("Function")}</th>
                <th className="align-middle">{t("Domain")}</th>
                <th className="align-middle">{t("Question")}</th>
                <th className="align-middle">{t("Bank Response")}</th>
                <th className="align-middle">{t("Control Type")}</th>
                <th className="align-middle">{t("Examples of Required Samples")}</th>
                <th className="align-middle">{t("Control Mapping")}</th>
                <th className="align-middle">{t("Bank Compliance Brief / Justification")}</th>
                <th className="align-middle">{t("Impacted/Affected Area")}</th>
                <th className="align-middle">{t("Owner")}</th>
              </tr>
            </thead>
            <tbody>
              {(loading || searchLoading) ? (
                <tr>
                  <td colSpan={11} style={{ backgroundColor: "#ffffff", color: "#000000", padding: '24px' }} className="text-center">
                    Loading...
                  </td>
                </tr>
              ) : filteredData.length > 0 ? (
                filteredData.map((risk: any, index: any) => (
                  <tr key={index}>
                    <td style={{ width: "3%", textAlign: "center" }}>{risk.index}</td>
                    <td style={{ width: "7%" }}>{risk.function}</td>
                    <td style={{ width: "7%" }}>{risk.domain}</td>
                    <td style={{ width: "22%", textAlign: "left" }}>{risk.question}</td>
                    <td style={{ width: "7%", textAlign: "center" }}>
                      <div className="bank-response-cell">
                        <span className="bank-response-text">{risk.bankResponse}</span>
                        {(canUpdate || isSuperAdmin) && (
                          <button
                            className="action-button"
                            onClick={() => openEditModal(risk)}
                            title="Edit Bank Response"
                          >
                            <MdOutlineEdit size={20} />
                          </button>
                        )}
                      </div>
                    </td>
                    <td style={{
                      width: "5%", textAlign: "center", 
                      backgroundColor: risk.controlType == 'Mandatory' ? "#dc4a4a" : risk.controlType == "Advisory" ? "#e8c412" : "#ffffff", 
                      color: risk.controlType == 'Mandatory' ? "#ffffff" : risk.controlType == "Advisory" ? "#000000" : "#000000"
                    }}>{risk.controlType}</td>
                    <td style={{ width: "18%", textAlign: "left" }}>
                      <span
                        onClick={() => openViewModal(t("Examples of Required Samples") || "Examples of Required Samples", risk.examples)}
                        style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
                        title={t("Click to view") || 'Click to view'}
                      >
                        {(risk.examples && risk.examples.length > 120) ? `${risk.examples.substring(0, 120)}...` : (risk.examples || 'N/A')}
                      </span>
                    </td>
                    <td style={{ width: "7%", textAlign: "left" }}>{renderClickableControls(risk)}</td>
                    <td style={{ width: "22%", textAlign: "left" }}>
                      <span
                        onClick={() => openViewModal(t("Bank Compliance Brief / Justification") || "Bank Compliance Brief / Justification", risk.complianceBrief)}
                        style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
                        title={t("Click to view") || 'Click to view'}
                      >
                        {(risk.complianceBrief && risk.complianceBrief.length > 120) ? `${risk.complianceBrief.substring(0, 120)}...` : (risk.complianceBrief || 'N/A')}
                      </span>
                    </td>
                    <td style={{ width: "7%" }}>{risk.impactedArea}</td>
                    <td style={{ width: "5%" }}>{risk.owner}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={11} style={{ backgroundColor: "#ffffff", color: "#000000" }} className="text-center">
                    {t("No data available.")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>

          {totalFiltered > 0 && (
            <div className="services-catalog-pagination-bottom d-flex justify-content-center mt-3">
              <DataMgmtCatalogPagination
                page={page + 1}
                totalPages={catalogTotalPages}
                totalFiltered={totalFiltered}
                busy={busyOverlay}
                onPageChange={handleCatalogPageChange}
              />
            </div>
          )}
        </>
      )}

      {notAllowed && <div className="container_info">
        <div className="d-flex justify-content-between my-2 text-center alert alert-danger">
          <h5 style={{ width: '100%' }} className="text-center">{t("permissions.not_allowed")}</h5>
        </div>
      </div>}

      {/* Edit Bank Response Modal */}
      <Modal isOpen={isEditModalOpen} toggle={closeEditModal} size="md">
        <div className={`modal-content ${isUpdating ? 'updating' : ''}`}>
          <div className="modal-header">
            <h5 className="modal-title">Edit Bank Response</h5>
            <button
              type="button"
              className="btn-close"
              onClick={closeEditModal}
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body">
            {editingItem && (
              <div>
                <div className="mb-3">
                  <label className="form-label fw-bold">Question:</label>
                  <p className="text-muted">{editingItem.question}</p>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold">Current Response:</label>
                  <p className="text-muted">{editingItem.bankResponse}</p>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold">Select New Response:</label>
                  <div className="choices-info">
                    <small>
                      Available choices: {bankResponseChoices.length > 0 ? bankResponseChoices.join(', ') : 'Loading...'}
                    </small>
                  </div>
                  <div className="row">
                    {bankResponseChoices.map((choice, index) => (
                      <div key={index} className="col-md-6 mb-2">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="bankResponse"
                            id={`choice-${index}`}
                            value={choice}
                            checked={selectedResponse === choice}
                            onChange={() => handleResponseChange(choice)}
                          />
                          <label className="form-check-label" htmlFor={`choice-${index}`}>
                            {choice}
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={closeEditModal}
              disabled={isUpdating}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleUpdateResponse}
              disabled={!selectedResponse || isUpdating || (!canUpdate && !isSuperAdmin)}
              title={(!canUpdate && !isSuperAdmin) ? "You don't have permission to update bank responses" : ""}
            >
              {isUpdating ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Updating...
                </>
              ) : (
                "Update Response"
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* View Full Content Modal */}
      <Modal isOpen={isViewModalOpen} toggle={closeViewModal} size="lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{viewModalTitle}</h5>
            <button
              type="button"
              className="btn-close"
              onClick={closeViewModal}
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body">
            <div
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: '60vh',
                overflowY: 'auto',
                background: '#f8f9fa',
                padding: '12px',
                borderRadius: '6px',
                border: '1px solid #dee2e6'
              }}
            >
              {viewModalContent || 'N/A'}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={closeViewModal}>
              {t('Close') || 'Close'}
            </button>
            <button type="button" className="btn btn-primary" onClick={copyViewContent}>
              {t('Copy') || 'Copy'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}